import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PaymentSuccessPopup from '../components/Payment/PaymentSuccessPopup';
import { PayPalService } from '../services/paypalService';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState<string | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Extract order ID and token from URL parameters
    const token = searchParams.get('token');
    const payerId = searchParams.get('PayerID');
    const orderIdParam = searchParams.get('orderId');

    const resolvedOrderId = (orderIdParam || token || '').trim() || undefined;

    const notifyOpener = (oid?: string) => {
      try {
        if (window.opener && !window.opener.closed && oid) {
          window.opener.postMessage(
            { type: 'PAYMENT_SUCCESS', orderId: oid },
            window.location.origin
          );
        }
      } catch {
        // ignore
      }
    };

    const waitForCompletion = async (oid: string, opts?: { attempts?: number; delayMs?: number }) => {
      const attempts = Math.max(1, opts?.attempts ?? 10);
      const delayMs = Math.max(250, opts?.delayMs ?? 750);
      for (let i = 0; i < attempts; i++) {
        const s = await PayPalService.checkPaymentStatus(oid);
        if (s.success && (s.completed || s.cancelled)) return s;
        await new Promise((r) => window.setTimeout(r, delayMs));
      }
      return await PayPalService.checkPaymentStatus(oid);
    };

    const processPayment = async () => {
      try {
        if (!resolvedOrderId) {
          setOrderId(undefined);
          return;
        }

        // Capture the order if possible
        if (payerId) {
          const exec = await PayPalService.executePayment(resolvedOrderId, payerId);
          if (exec.success && exec.orderId) {
            setOrderId(exec.orderId);
            notifyOpener(exec.orderId);
            await waitForCompletion(exec.orderId);
          } else {
            // If capture fails, fall back to status polling
            setOrderId(resolvedOrderId);
            const s = await waitForCompletion(resolvedOrderId);
            if (s.success && s.completed) notifyOpener(resolvedOrderId);
          }
        } else {
          // No payerId available, fall back to status polling
          setOrderId(resolvedOrderId);
          const s = await waitForCompletion(resolvedOrderId);
          if (s.success && s.completed) notifyOpener(resolvedOrderId);
        }
      } catch (error) {
        console.error('Error processing payment:', error);
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'radial-gradient(circle at top, rgba(16,185,129,0.14) 0%, rgba(255,255,255,1) 42%, rgba(255,255,255,1) 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#111' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at top, rgba(16,185,129,0.14) 0%, rgba(255,255,255,1) 42%, rgba(255,255,255,1) 100%)',
        p: 2,
      }}
    >
      <PaymentSuccessPopup
        open={true}
        orderId={orderId}
        amount={amount}
      />
    </Box>
  );
};

export default PaymentSuccessPage;

